import { ParsedUrlQuery } from 'querystring'
import { PartnerPageData } from 'types/types'

export const DEFAULT_CHARITY_SLUG = 'charity'

/**
 * Charity page URL segment for a partner, derived by the API from "Charity Page Breadcrumb Name".
 * Falls back to the legacy `/charity` segment when the API doesn't provide one.
 */
export function getCharitySlug(
  page: Pick<PartnerPageData, 'charity_slug'> | null | undefined,
): string {
  return page?.charity_slug || DEFAULT_CHARITY_SLUG
}

export function getCharityPath(
  page: Pick<PartnerPageData, 'company_domain' | 'charity_slug'>,
): string {
  return `/${page.company_domain}/${getCharitySlug(page)}`
}

/**
 * Where a request for `/{slug}/{requestedSlug}` should go, given the partner's configured charity slug.
 * - `render`: requested slug is the canonical one.
 * - `redirect`: legacy `/charity` hit for a partner with a custom slug.
 * - `notFound`: anything else.
 */
export function resolveCharityRoute(
  requestedSlug: string,
  page: Pick<PartnerPageData, 'charity_slug'>,
): 'render' | 'redirect' | 'notFound' {
  const canonicalSlug = getCharitySlug(page)
  if (requestedSlug === canonicalSlug) return 'render'
  if (requestedSlug === DEFAULT_CHARITY_SLUG) return 'redirect'
  return 'notFound'
}

/** Swap the last path segment of `resolvedUrl` for `charitySlug`, keeping any query string. */
export function buildCanonicalCharityUrl(resolvedUrl: string, charitySlug: string): string {
  const [pathname, query] = resolvedUrl.split('?')
  const segments = pathname.split('/')
  segments[segments.length - 1] = charitySlug
  const destination = segments.join('/')
  return query ? `${destination}?${query}` : destination
}

/**
 * `router.query` without the charity page's `charitySlug` route param, so spreading it into another page's query
 * (e.g. the header's login links) doesn't leak it into the URL as `?charitySlug=…`.
 */
export function withoutCharitySlug(query: ParsedUrlQuery): ParsedUrlQuery {
  const rest = { ...query }
  delete rest.charitySlug
  return rest
}
